import type React from "react";
import { Fragment, useMemo } from "react";
import { Alert, Box, Group, Stack, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { MappingStatus, type JobFileViewModel, type RowEdit } from "@models/job";
import type { MappingResultRowTm } from "@models/mappingResult";
import { resolveDisplayTarget } from "@utils/rowHelpers";
import { MAPPING_STATUS_BADGE, tmNoMatchBadge, type StatusBadgeSpec } from "@utils/resultStatusBadges";
import StatusBadge from "../StatusBadge";
import SourcePdfLink from "../../SourcePdfLink";
import {
  alignCompareLines,
  groupTmBatchRows,
  orderTmBatchFiles,
  type CompareLine,
  type FileValueGroup,
  type TmBatchGroup,
} from "./tmBatchGroups";
import "./TmBatchCompare.scss";

interface TmBatchCompareProps {
  jobId: string;
  rows: MappingResultRowTm[];
  files: JobFileViewModel[];
  edits: Map<number, RowEdit>;
  differentOnly: boolean;
}

const displayValue = (row: MappingResultRowTm, edit: RowEdit | undefined) => {
  const target = resolveDisplayTarget(row, edit);
  const sourceValue = row.source.cleanedText || row.source.rawText;
  const value = row.mappingStatus === MappingStatus.NoMatch
    ? sourceValue
    : (target?.valueDisplay || sourceValue);
  const itemId = target?.itemMpId;
  return {
    value,
    unit: target?.unit ?? null,
    text: itemId != null && value
      ? `${itemId} : ${value}`
      : (value || (itemId != null ? String(itemId) : "—")),
  };
};

/** Collapse repeated visible values within one PDF, never across PDFs or units.
 *  The key is what the reviewer sees (edits included), so two cells showing
 *  the same value are always equal. */
const groupFileValues = (rows: MappingResultRowTm[], edits: Map<number, RowEdit>): FileValueGroup[] => {
  const groups: FileValueGroup[] = [];
  const byValue = new Map<string, FileValueGroup>();
  for (const row of rows) {
    const shown = displayValue(row, edits.get(row.rowIndex));
    if (row.mappingStatus === MappingStatus.Failed || !shown.value) {
      groups.push({ key: null, rows: [row] });
      continue;
    }
    const key = JSON.stringify([shown.text, shown.unit]);
    let group = byValue.get(key);
    if (!group) {
      group = { key, rows: [] };
      byValue.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
};

const joinDistinct = (values: (string | null)[]): string =>
  [...new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value))].join("; ");

const CompareValue: React.FC<{ rows: MappingResultRowTm[]; edit: RowEdit | undefined }> = ({ rows, edit }) => {
  const { t } = useTranslation();
  const row = rows[0];
  return (
    <Text size="sm" fw={600} title={joinDistinct(rows.map((item) => item.source.path)) || undefined}>
      {row.mappingStatus === MappingStatus.Failed
        ? (row.error?.message ?? t("resultRow.status.failed"))
        : displayValue(row, edit).text}
    </Text>
  );
};

// The vehicle is its model code and engine (see TM_BATCH_GROUP_FIELDS).
const FIXED_COLUMNS = ["Model Code", "Engine Code", "Displacement", "Fuel", "Cylinders", "Engine type"];
const FIXED_WIDTHS = [120, 130, 120, 90, 100, 300];
const FILE_COLUMNS = ["value", "unit", "remark", "countryCodes", "status"] as const;
const FILE_WIDTHS = [230, 65, 145, 125, 150];
const FIXED_WIDTH = FIXED_WIDTHS.reduce((total, width) => total + width, 0);
const FILE_WIDTH = FILE_WIDTHS.reduce((total, width) => total + width, 0);

const TmBatchCompare: React.FC<TmBatchCompareProps> = ({
  rows, files, edits, differentOnly, jobId
}) => {
  const { t } = useTranslation();
  const { groups, unattributed } = useMemo(() => groupTmBatchRows(rows, files), [rows, files]);
  const orderedFiles = useMemo(() => orderTmBatchFiles(files, rows), [files, rows]);
  const comparableIds = orderedFiles
    .filter((file) => file.status === "Completed")
    .map((file) => file.fileIndex);
  const fileOrder = orderedFiles.map((file) => file.fileIndex);
  const linesOf = new Map<string, CompareLine[]>(groups.map((group) => [
    group.key,
    alignCompareLines(
      new Map([...group.byFile].map(([fileId, fileRows]) => [fileId, groupFileValues(fileRows, edits)])),
      fileOrder,
      comparableIds,
      differentOnly,
    ),
  ]));
  // With "different values only", an item whose every value is shared has no
  // line left, and so no table.
  const compareGroups = groups.filter((group) => (linesOf.get(group.key)?.length ?? 0) > 0);
  const vehicles = new Map<string, TmBatchGroup[]>();
  for (const group of compareGroups) {
    const vehicleGroups = vehicles.get(group.vehicleKey) ?? [];
    vehicleGroups.push(group);
    vehicles.set(group.vehicleKey, vehicleGroups);
  }

  return (
    <Stack gap="md" className="tm-batch-compare">
      <Text size="xs" c="dimmed">{t("jobResultsPage.batchCompare.groupCount", { count: compareGroups.length })}</Text>

      {unattributed.length > 0 && (
        <Alert color="yellow" title={t("jobResultsPage.batchCompare.unattributed", { count: unattributed.length })} />
      )}
      <Stack gap="md" role="region" aria-label={t("jobResultsPage.batchCompare.compareFiles")}>
        {[...vehicles.values()].map((vehicleGroups) => {
          const identity = vehicleGroups[0].identity;
          const vehicleLabel = t("jobResultsPage.batchCompare.vehicleTable", {
            identity: FIXED_COLUMNS.map((label, index) => `${label}: ${identity[index] || "—"}`).join(", "),
          });
          return (
            <Box
              key={vehicleGroups[0].vehicleKey}
              role="region"
              aria-label={vehicleLabel}
              tabIndex={0}
              className="tm-batch-compare__table-wrap"
            >
              <Table
                withColumnBorders
                className="tm-batch-compare__table"
                aria-label={vehicleLabel}
                style={{ minWidth: FIXED_WIDTH }}
              >
                <colgroup>
                  {FIXED_COLUMNS.map((label, index) => <col key={label} style={{ width: FIXED_WIDTHS[index] }} />)}
                </colgroup>
                <Table.Thead>
                  <Table.Tr>
                    {FIXED_COLUMNS.map((label) => <Table.Th key={label} scope="col">{label}</Table.Th>)}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr className="tm-batch-compare__vehicle-row">
                    {identity.map((field, fieldIndex) => (
                      <Table.Td key={FIXED_COLUMNS[fieldIndex]}>{field || "—"}</Table.Td>
                    ))}
                  </Table.Tr>
                  {vehicleGroups.map((group: TmBatchGroup, index) => {
                    const lines = linesOf.get(group.key) ?? [];
                    const groupColor = index % 2 === 0
                      ? "tm-batch-compare__group--blue"
                      : "tm-batch-compare__group--green";
                    const listing = group.itemMpListing || t(
                      group.itemMpId != null
                        ? "jobResultsPage.batchCompare.listingUnavailable"
                        : "jobResultsPage.batchCompare.unmapped",
                    );
                    return (
                <Fragment key={group.key}>
                  <Table.Tr className={`tm-batch-compare__item-row ${groupColor}`}>
                    <Table.Td colSpan={FIXED_COLUMNS.length}>
                      <Group gap="xl" wrap="wrap" justify="center">
                        <Text size="sm" fw={600}>Item-MP Listing: <Text span fw={400}>{listing}</Text></Text>
                        <Text size="sm" fw={600}>ItemMP ID: <Text span fw={400}>{group.itemMpId ?? "—"}</Text></Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr className={`tm-batch-compare__alternatives-row ${groupColor}`}>
                    <Table.Td colSpan={FIXED_COLUMNS.length} className="tm-batch-compare__alternatives-cell">
                      <Box
                        role="region"
                        aria-label={t("jobResultsPage.batchCompare.alternativesFor", {
                          item: listing,
                        })}
                        tabIndex={0}
                        className="tm-batch-compare__alternatives-scroll"
                      >
                        <Table
                          withColumnBorders
                          className="tm-batch-compare__alternatives-table"
                          style={{ width: orderedFiles.length * FILE_WIDTH }}
                        >
                          <colgroup>
                            {orderedFiles.flatMap((file) => FILE_COLUMNS.map((column, index) => (
                              <col key={`${file.fileIndex}-${column}`} style={{ width: FILE_WIDTHS[index] }} />
                            )))}
                          </colgroup>
                          <Table.Thead>
                            <Table.Tr>
                              {orderedFiles.map((file, index) => (
                                <Table.Th
                                  key={file.fileIndex}
                                  colSpan={FILE_COLUMNS.length}
                                >
                                  <Text component="span" fw={700} size="sm">
                                    {t("jobResultsPage.batchCompare.fileNumber", { number: index + 1 })}:
                                  </Text>{" "}
                                  <SourcePdfLink
                                    jobId={jobId}
                                    fileIndex={file.fileIndex}
                                    filename={file.memberPath ?? file.filename}
                                    hasInputPdf={file.hasInputPdf}
                                  />
                                </Table.Th>
                              ))}
                            </Table.Tr>
                            <Table.Tr>
                              {orderedFiles.flatMap((file) => FILE_COLUMNS.map((column) => (
                                <Table.Th key={`${file.fileIndex}-${column}`}>
                                  {t(`jobResultsPage.batchCompare.columns.${column}`)}
                                </Table.Th>
                              )))}
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {lines.map((line, occurrence) => (
                              <Table.Tr key={occurrence}>
                                {orderedFiles.flatMap((file) => {
                                  const groupedRows = file.status === "Completed"
                                    ? line.get(file.fileIndex) : undefined;
                                  const row = groupedRows?.[0];
                                  const findings = new Map<string, StatusBadgeSpec>();
                                  for (const item of groupedRows ?? []) {
                                    const finding = item.mappingStatus === MappingStatus.NoMatch
                                      ? tmNoMatchBadge(item.reason)
                                      : item.mappingStatus === MappingStatus.AutoAccepted
                                        ? MAPPING_STATUS_BADGE[MappingStatus.AutoAccepted] : null;
                                    if (finding) findings.set(finding.badgeKey, finding);
                                  }
                                  return [
                                    <Table.Td key={`${file.fileIndex}-value`}>
                                      {file.status !== "Completed"
                                        ? occurrence === 0 && <Text size="xs" c={file.status === "Failed" ? "red" : "dimmed"}>{file.status === "Failed" ? t("jobResultsPage.batchCompare.fileFailed") : file.status}</Text>
                                        : groupedRows
                                          ? <CompareValue rows={groupedRows} edit={edits.get(groupedRows[0].rowIndex)} />
                                          : occurrence === 0 && !group.byFile.has(file.fileIndex)
                                            && <Text size="xs" c="dimmed">{t("jobResultsPage.batchCompare.notPresent")}</Text>}
                                    </Table.Td>,
                                    <Table.Td key={`${file.fileIndex}-unit`}>
                                      {row && displayValue(row, edits.get(row.rowIndex)).unit}
                                    </Table.Td>,
                                    <Table.Td key={`${file.fileIndex}-remark`}>
                                      {joinDistinct(groupedRows?.map((item) => item.source.remark) ?? [])}
                                    </Table.Td>,
                                    <Table.Td key={`${file.fileIndex}-countryCodes`}>
                                      {joinDistinct(groupedRows?.map((item) => item.source.countryCodes) ?? [])}
                                    </Table.Td>,
                                    <Table.Td key={`${file.fileIndex}-status`}>
                                      {findings.size > 0 && (
                                        <Group gap="xs">
                                          {Array.from(findings.values(), (finding) => (
                                            <StatusBadge
                                              key={finding.badgeKey}
                                              spec={finding}
                                              text={t(finding.badgeKey, { defaultValue: finding.badge })}
                                              label={t(finding.labelKey, { defaultValue: finding.fallback })}
                                            />
                                          ))}
                                        </Group>
                                      )}
                                    </Table.Td>,
                                  ];
                                })}
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Box>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              );
            })}
                </Table.Tbody>
              </Table>
            </Box>
          );
        })}
      </Stack>
      {compareGroups.length === 0 && <Text c="dimmed">{t("jobResultsPage.batchCompare.noGroups")}</Text>}
    </Stack>
  );
};

export default TmBatchCompare;
